import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/auth';
import { 
  LayoutDashboard, 
  Beaker, 
  Building2, 
  BarChart3,
  Flame,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Layers,
  LogOut,
  BookOpen
} from 'lucide-react';

const Sidebar = ({ isOpen = false, onClose, isMobile = false, isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [logoFailed, setLogoFailed] = useState(false);

  const logoSrc = `${process.env.PUBLIC_URL || ''}/AAA_logo.jpeg`;

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      roles: ['executive_director', 'plant_manager', 'operator'],
      description: 'View LCA results and analytics'
    },
    {
      id: 'scenarios',
      label: 'Scenarios',
      path: '/scenarios',
      icon: Beaker,
      roles: ['executive_director', 'plant_manager', 'operator'],
      description: 'Manage LCA scenarios',
      badge: null
    },
    {
      id: 'aggregate',
      label: 'Comparative Analysis',
      path: '/aggregate',
      icon: BarChart3,
      roles: ['executive_director', 'plant_manager', 'operator'],
      description: 'Compare multiple scenarios'
    },
    {
      id: 'hotspot',
      label: 'Hotspot Emissions',
      path: '/hotspot',
      icon: Flame,
      roles: ['executive_director', 'plant_manager', 'operator'],
      description: 'Identify emission hotspots'
    },
    // {
    //   id: 'facilities',
    //   label: 'Facilities',
    //   path: '/facilities',
    //   icon: Building2,
    //   roles: ['executive_director', 'plant_manager', 'operator'],
    //   description: 'Production facilities'
    // }
  ];

  const footerItems = [
    {
      id: 'user-manual',
      label: 'User Manual',
      path: '/user-manual',
      icon: BookOpen,
      roles: ['executive_director', 'plant_manager', 'operator'],
      description: 'System user guide and documentation'
    }
    // {
    //   id: 'settings',
    //   label: 'Settings',
    //   path: '/settings',
    //   icon: Settings
    // },
    // {
    //   id: 'help',
    //   label: 'Help & Support',
    //   path: '/help',
    //   icon: HelpCircle
    // }
  ];

  const isActive = (path) => location.pathname === path;

  const canAccessItem = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleToggleCollapse = () => {
    if (!isMobile && onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const handleLogout = () => {
    logout();
    if (isMobile && onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (isMobile && onClose) {
      onClose();
    }
  }, [location.pathname, isMobile, onClose]);

  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <aside className={`sidebar sidebar-gradient ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${isOpen ? 'open' : ''}`}>
        {/* Logo Area with Image Support */}
        <div className="sidebar-logo" role="banner">
          <div className="sidebar-logo-container">
            {!logoFailed ? (
              <img
                src={logoSrc}
                alt="AAA Asphalt LCA"
                className="sidebar-logo-image"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div className="sidebar-logo-fallback" aria-hidden="true">
                <Layers size={64} />
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-group">
            <div className="nav-group-label">Main Menu</div>
            <div className="nav-items">
              {menuItems.map((item) => {
                if (!canAccessItem(item)) return null;
                
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <div 
                    key={item.id}
                    className="nav-item-wrapper"
                    onMouseEnter={() => setHoveredItem(item.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <button
                      className={`nav-item ${active ? 'active' : ''}`}
                      onClick={() => handleNavigation(item.path)}
                      title={item.description}
                    >
                      <div className="nav-item-icon-wrapper">
                        <Icon className="nav-item-icon" size={20} />
                      </div>
                      <span className="nav-item-text">{item.label}</span>
                      {item.badge && (
                        <span className="nav-item-badge">{item.badge}</span>
                      )}
                    </button>
                    
                    {isCollapsed && !isMobile && hoveredItem === item.id && (
                      <div className="nav-item-tooltip">
                        <div className="tooltip-arrow"></div>
                        {item.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {isMobile && user && (
            <div className="nav-group mobile-user-section">
              <div className="nav-group-label">Account</div>
              <div className="user-info-card">
                <div className="avatar">
                  {getUserInitials()}
                </div>
                <div className="user-info-text">
                  <div className="user-name">
                    {user.name || user.email}
                  </div>
                  <div className="user-role">
                    {user.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
        
        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-items">
            {footerItems.map((item) => {
              const Icon = item.icon;
              
              return (
                <div 
                  key={item.id}
                  className="nav-item-wrapper"
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <button
                    className="sidebar-footer-item"
                    onClick={() => handleNavigation(item.path)}
                    title={item.label}
                  >
                    <div className="nav-item-icon-wrapper">
                      <Icon className="sidebar-footer-icon" size={18} />
                    </div>
                    <span className="sidebar-footer-text">{item.label}</span>
                  </button>
                  
                  {isCollapsed && !isMobile && hoveredItem === item.id && (
                    <div className="nav-item-tooltip">
                      <div className="tooltip-arrow"></div>
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
            
            <div 
              className="nav-item-wrapper"
              onMouseEnter={() => setHoveredItem('logout')}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                className="sidebar-footer-item logout-item"
                onClick={handleLogout}
                title="Sign Out"
              >
                <div className="nav-item-icon-wrapper">
                  <LogOut className="sidebar-footer-icon" size={18} />
                </div>
                <span className="sidebar-footer-text">Sign Out</span>
              </button>
              
              {isCollapsed && !isMobile && hoveredItem === 'logout' && (
                <div className="nav-item-tooltip">
                  <div className="tooltip-arrow"></div>
                  Sign Out
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Toggle Button (Desktop only) */}
        {!isMobile && (
          <button 
            className="sidebar-toggle"
            onClick={handleToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="sidebar-toggle-icon" size={16} />
            ) : (
              <ChevronLeft className="sidebar-toggle-icon" size={16} />
            )}
          </button>
        )}
      </aside>
      
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div 
          className="mobile-sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Sidebar;